// src/utils/htmlHead.utils.js

import { shopInfoContainer } from '@/containers/shopInfo.container';
import { extractFirstSearchParamValue, findKeyFromValue } from '@/utils/functions.utils';
import { codeSetMapping } from '@/containers/Codes';


/**
 * Creates structured data for a product page
 * @param {object} product - Product object with details
 * @param {string} lang - Language code
 * @param {string} url - Full URL of the product page
 * @returns {object} - JSON-LD schema for the product
 */
export function createProductSchema(product, lang, url) {
  if (!product) return null;

  // Get shop info for organization data
  const { baseURL, googleMapsPlaceID, name: shopName, address, phone } = shopInfoContainer;
  const unitSystem = lang === 'en' ? 'USC' : 'SI';
  const titles = product.titles[unitSystem];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product?.titleAtCheckout || titles?.standard,
    description: titles?.altTag?.detailed || titles?.altTag?.basic,
    image: product?.mainImage?.png?.url,
    sku: product?.sku  || product?.productID,
    mpn: product?.productID,
    brand: {
      '@type': 'Brand',
      name: product.brand || 'YourCarpet.com'
    },
    category: `Carpet > ${product.style || 'Decorative'} > ${product.origins || 'Imported'}`,
    url: url,
    offers: {
      '@type': 'Offer',
      url: url,
      priceCurrency: 'EUR',
      price: product.price,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      availability: product.availability || product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: shopName
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: product.shippingPrice || 0,
          currency: 'EUR'
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY'
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 5,
            unitCode: 'DAY'
          }
        }
      }
    }
  };

  // Add material if available
  if (product.materials && product.materials.length) {
    schema.material = product.materials.join(', ');
  }

  // Add product dimensions
  if (product.width && product.length) {
    schema.width = {
      '@type': 'QuantitativeValue',
      value: product.width,
      unitCode: 'CMT'
    };
    schema.height = {
      '@type': 'QuantitativeValue',
      value: product.length,
      unitCode: 'CMT'
    };
  }

  // Add origin as country of origin if available
  if (product.origins) {
    schema.countryOfOrigin = {
      '@type': 'Country',
      name: product.origins
    };
  }

  // Add multiple offers for different currencies if needed
  if (product.priceGBP || product.priceUSD) {
    schema.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: Math.min(product.price, product.priceGBP || Infinity, product.priceUSD || Infinity),
      highPrice: product.price,
      offerCount: 1 + (product.priceGBP ? 1 : 0) + (product.priceUSD ? 1 : 0),
      offers: [
        {
          '@type': 'Offer',
          url: url,
          priceCurrency: 'EUR',
          price: product.price,
          priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          availability: product.availability || product.stock > 0 
            ? 'https://schema.org/InStock' 
            : 'https://schema.org/OutOfStock',
          seller: {
            '@type': 'Organization',
            name: shopName
          }
        }
      ]
    };

    // Add GBP offer if available
    if (product.priceGBP) {
      schema.offers.offers.push({
        '@type': 'Offer',
        url: url,
        priceCurrency: 'GBP',
        price: product.priceGBP,
        priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        availability: product.availability || product.stock > 0 
          ? 'https://schema.org/InStock' 
          : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: shopName
        }
      });
    }

    // Add USD offer if available
    if (product.priceUSD) {
      schema.offers.offers.push({
        '@type': 'Offer',
        url: url,
        priceCurrency: 'USD',
        price: product.priceUSD,
        priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        availability: product.availability || product.stock > 0 
          ? 'https://schema.org/InStock' 
          : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: shopName
        }
      });
    }
  }

  // Add review aggregate if available
  if (product.rating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      ratingCount: product.ratingCount || 5,
      bestRating: 5,
      worstRating: 1
    };
  }

  return schema;
}

/**
 * Creates structured data for the organization
 * @returns {object} - JSON-LD schema for the organization
 */
export function createOrganizationSchema() {
  const { 
    baseURL, 
    googleMapsPlaceID, 
    name: shopName, 
    address, 
    phone, 
    logo,
    openingHours 
  } = shopInfoContainer;

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseURL}/#organization`,
    name: shopName,
    url: baseURL,
    logo: `${baseURL}/logo.png`,
    telephone: phone,
    email: 'info@yourcarpet.com',
    sameAs: [
      'https://www.facebook.com/yourcarpet',
      'https://www.instagram.com/yourcarpet',
      'https://www.pinterest.com/yourcarpet'
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      addressLocality: address.city,
      postalCode: address.postalCode,
      addressCountry: address.country
    }
  };
}

/**
 * Creates structured data for breadcrumbs navigation
 * @param {array} items - Array of breadcrumb items with name and url
 * @returns {object} - JSON-LD schema for breadcrumbs
 */
export function createBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}

/**
 * Creates a FAQ schema for FAQ pages
 * @param {array} faqs - Array of question/answer pairs
 * @returns {object} - JSON-LD schema for FAQs
 */
export function createFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

/**
 * Creates schema for a store location
 * @param {string} storeName - Name of the store
 * @param {object} storeInfo - Store location details
 * @returns {object} - JSON-LD schema for store
 */
export function createLocalBusinessSchema(storeName, storeInfo) {
  const { 
    baseURL, 
    name: shopName,
    phone
  } = shopInfoContainer;

  return {
    '@context': 'https://schema.org',
    '@type': 'CarpetStore',
    '@id': `${baseURL}/stores/${storeName}#LocalBusiness`,
    name: `${shopName} - ${storeInfo.location}`,
    url: `${baseURL}/stores/${storeName}`,
    telephone: storeInfo.phone || phone,
    email: storeInfo.email || 'info@yourcarpet.com',
    image: storeInfo.image && [`${baseURL}${storeInfo.image}`],
    address: {
      '@type': 'PostalAddress',
      streetAddress: storeInfo.address.street,
      addressLocality: storeInfo.address.city,
      postalCode: storeInfo.address.postalCode,
      addressCountry: storeInfo.address.country
    },
    geo: storeInfo.coordinates && {
      '@type': 'GeoCoordinates',
      latitude: storeInfo.coordinates.lat,
      longitude: storeInfo.coordinates.lng
    },
    openingHoursSpecification: storeInfo.openingHours && storeInfo.openingHours.map(hours => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: hours.days,
      opens: hours.opens,
      closes: hours.closes
    })),
    paymentAccepted: 'Cash, Credit Card'
  };
}

/**
 * Creates a combined schema with multiple elements
 * @param {array} schemas - Array of schema objects
 * @returns {string} - Stringified JSON-LD
 */
export function createCombinedSchema(schemas) {
  // Filter out any null schemas
  const validSchemas = schemas.filter(schema => schema !== null);

  // Return stringified schema or @graph of multiple schemas
  if (validSchemas.length === 0) {
    return '{}';
  } else if (validSchemas.length === 1) {
    return JSON.stringify(validSchemas[0]);
  } else {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': validSchemas
    });
  }
}


function getLastModifiedDate(codeSetName) {
  // This can be improved to pull actual last modified dates from your CMS or database
  // For now, we'll use a static date to satisfy crawlers or use the current date

  // Option 1: Return a static date known to be when content was last updated
  // return "2025-03-01T10:30:00Z";

  // Option 2: Return current date - using this approach by default
  return new Date().toISOString();

  // Option 3: In a real implementation, you might look up the date from your data:
  // if (codeSetName && stylesCodes.data[codeSetName]?.lastUpdated) {
  //   return stylesCodes.data[codeSetName].lastUpdated;
  // }
}


// Helper function to extract metadata from htmlHead based on language
function extractMetadata(htmlHead, lang) {
  return {
    description: htmlHead.description?.[lang] || htmlHead.description?.['en'] || '',
    keywords: htmlHead.keywords?.[lang] || htmlHead.keywords?.['en'] || [],
    title: htmlHead.title?.[lang] || htmlHead.title?.['en'] || '',
    subtitle: htmlHead.subtitle?.[lang] || htmlHead.subtitle?.['en'] || '',
    ogDescription: htmlHead.og?.[lang] || htmlHead.og?.['en'] || '',
    image: htmlHead.image || 'https://www.yourcarpet.com/assets/open-graph/main-og.png',
    schemaType: htmlHead.schema?.['@type'] || 'CollectionPage',
    schemaDescription: htmlHead.schema?.[lang] || htmlHead.schema?.['en'] || ''
  };
}


export function populateBasedOnSearchParams(searchParams, lang) {

  // Define parameter priority (which params to check first)
  const paramPriority = ['styles', 'mainColors', 'weavedMaterials', 'origins', 'manufacturing', 'usage'];

  // Check each parameter type in priority order
  for (const paramKey of paramPriority) {
    const value = searchParams[paramKey];
    if (!value) continue;

    const codeSet = codeSetMapping[paramKey];
    if (!codeSet) continue;

    // Find the key based on the parameter type
    let key = null;
    if (paramKey === 'styles') {
      // Use existing function for styles for backward compatibility
      key = findKeyFromValue(value);
    } else {
      // Use codeSet method for other parameters
      key = codeSet.getKeyByName(value);
    }

    if (key === null) continue;

    const itemData = codeSet.data[key];
    if (!itemData) continue;

    // Initialize default values
    let description = '';
    let keywords = [];
    let title = '';
    let subtitle = '';
    let ogDescription = '';
    let image = 'https://www.yourcarpet.com/assets/open-graph/main-og.png';
    let schemaType = 'CollectionPage';
    let schemaDescription = '';

    // Extract metadata from htmlHead if it exists
    if (itemData.htmlHead) {
      const metadata = extractMetadata(itemData.htmlHead, lang);
      description = metadata.description;
      keywords = metadata.keywords;
      title = metadata.title;
      subtitle = metadata.subtitle;
      ogDescription = metadata.ogDescription;
      image = metadata.image;
      schemaType = metadata.schemaType;
      schemaDescription = metadata.schemaDescription;
    }

    // Try to get title and description from other fields if not found in htmlHead
    if (!title && itemData.name) {
      title = itemData.name[lang] || itemData.name['en'] || '';
    }

    if (!description && itemData.summary) {
      description = itemData.summary[lang] || itemData.summary['en'] || '';
    }

    const fullTitle = `${title} - YourCarpet.com`;

    // Build the path for this parameter
    const paramPath = `/en/shop/carpets?${paramKey}=${value}`;

    // Get last modified date
    const lastModified = getLastModifiedDate(key);

    // Build and return the metadata object
    return {
      title: {
        default: fullTitle,
        absolute: fullTitle
      },
      description,
      keywords,
      openGraph: {
        title: fullTitle,
        description: ogDescription || description,
        images: [{ url: image, alt: title }],
        url: paramPath,
        type: 'website',
        siteName: 'YourCarpet.com',
        updated_time: lastModified
      },
      twitter: {
        card: 'summary_large_image',
        title: fullTitle,
        description: ogDescription || description,
        images: [image]
      },
      alternates: {
        canonical: paramPath
      },
      other: {
        'subtitle': subtitle
      },
      schema: {
        '@context': 'https://schema.org',
        '@type': schemaType,
        name: title,
        description: schemaDescription || description,
        url: `https://www.yourcarpet.com${paramPath}`,
        image: image,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.yourcarpet.com${paramPath}`
        },
        dateModified: lastModified
      }
    };
  }

  // If no specific parameter was found, try the original approach for backward compatibility
  const value = extractFirstSearchParamValue(searchParams);
  if (!value) return {};

  const key = findKeyFromValue(value);
  if (key === null) return {};

  const styleData = codeSetMapping.styles.data[key];
  if (!styleData) return {};

  // This is the original logic, which should execute only if none of the specific parameters matched
  // Initialize default values
  let description = '';
  let keywords = [];
  let title = '';
  let subtitle = '';
  let ogDescription = '';
  let image = 'https://www.yourcarpet.com/assets/open-graph/main-og.png';
  let twitterDescription = '';
  let schemaType = 'CollectionPage';
  let schemaDescription = '';

  // Extract metadata from htmlHead if it exists
  if (styleData.htmlHead) {
    // Get the metadata values in the requested language, fallback to English
    description = styleData.htmlHead.description?.[lang] || styleData.htmlHead.description?.['en'] || '';
    keywords = styleData.htmlHead.keywords?.[lang] || styleData.htmlHead.keywords?.['en'] || [];
    title = styleData.htmlHead.title?.[lang] || styleData.htmlHead.title?.['en'] || '';
    subtitle = styleData.htmlHead.subtitle?.[lang] || styleData.htmlHead.subtitle?.['en'] || '';

    // Extract OpenGraph data
    ogDescription = styleData.htmlHead.og?.[lang] || styleData.htmlHead.og?.['en'] || description;
    image = styleData.htmlHead.image || image;

    // Extract Twitter data
    twitterDescription = styleData.htmlHead.twitter?.[lang] || styleData.htmlHead.twitter?.['en'] || ogDescription;

    // Extract Schema data
    schemaType = styleData.htmlHead.schema?.['@type'] || schemaType;
    schemaDescription = styleData.htmlHead.schema?.[lang] || styleData.htmlHead.schema?.['en'] || description;
  }

  // Try to get title and description from other fields if not found in htmlHead
  if (!title && styleData.name) {
    title = styleData.name[lang] || styleData.name['en'] || '';
  }

  if (!description && styleData.summary) {
    description = styleData.summary[lang] || styleData.summary['en'] || '';
  }

  // If we still don't have a title or description, return empty object
  if (!title && !description) return {};

  const fullTitle = `${title} - YourCarpet.com`;

  // If ogDescription or twitterDescription weren't set, use description
  if (!ogDescription) ogDescription = description;
  if (!twitterDescription) twitterDescription = ogDescription;

  // Build the path for this style
  const stylePath = `/en/shop/carpets?styles=${value}`;

  // Get last modified date
  const lastModified = getLastModifiedDate(key);

  return {
    title: {
      default: fullTitle,
      absolute: fullTitle
    },
    description,
    keywords,
    openGraph: {
      title: fullTitle,
      description: ogDescription,
      images: [{ url: image, alt: title }],
      url: stylePath,
      type: 'website',
      siteName: 'YourCarpet.com',
      updated_time: lastModified
    },
    twitter: {
      card: 'summary_large_image',
      title: fullTitle,
      description: twitterDescription,
      images: [image]
    },
    alternates: {
      canonical: stylePath
    },
    other: {
      'subtitle': subtitle
    },
    schema: {
      '@context': 'https://schema.org',
      '@type': schemaType,
      name: title,
      description: schemaDescription,
      url: `https://www.yourcarpet.com${stylePath}`,
      image: image,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.yourcarpet.com${stylePath}`
      },
      dateModified: lastModified
    }
  };
}


/**
 * Update the JSON-LD generation to include freshness information
 */
export function generateJSONLD(params, lang) {

  // Extract the necessary parameters from the component state
  const { pageTitle, pageSubtitle, searchParams, initialData, codeSetName } = params;

  const codeSet = codeSetMapping[codeSetName];  // e.g. 'styles' goes in and stylesCodes comes out

  // Base URL for the site
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.yourcarpet.com';

  // Construct the full URL including search parameters
  const searchParamsString = new URLSearchParams(searchParams).toString();
  const path = `/shop/carpets`;
  const fullPath = searchParamsString ? `${path}?${searchParamsString}` : path;
  const pageUrl = `${baseUrl}/${lang}${fullPath}`;

  // Get style-specific information if available
  let description = '';
  let image = 'https://www.yourcarpet.com/assets/open-graph/main-og.png';

  if (codeSetName !== null && codeSet) {
    const itemData = codeSet.data[codeSetName];
    if (itemData) {
      // Get description in the requested language, fallback to English
      if (itemData.htmlHead) {
        description = itemData.htmlHead.description?.[lang] || itemData.htmlHead.description?.['en'] || '';
        image = itemData.htmlHead.image || image;
      }

      // Get summary in the requested language, fallback to English
      if (!description && itemData.summary) {
        description = itemData.summary[lang] || itemData.summary['en'] || '';
      }
    }
  }

  // Get last modified date
  const lastModified = getLastModifiedDate(codeSetName);

  // Get product information from initialData if available
  const itemListElement = [];
  if (initialData && initialData.data && Array.isArray(initialData.data)) {
    initialData.data.forEach((product, index) => {
      if (product.id && product.name) {
        itemListElement.push({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Product",
            "name": product.name,
            "url": `${baseUrl}/${lang}/product/${product.id}`,
            "image": product.images && product.images[0] ? product.images[0] : image,
            "offers": {
              "@type": "Offer",
              "price": product.price || "",
              "priceCurrency": product.currency || "USD",
              "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
            }
          }
        });
      }
    });
  }

  // Create the structured data object
  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "description": description || `Shop our collection of ${pageTitle.toLowerCase()} at YourCarpet.com`,
    "url": pageUrl,
    "image": image,
    "inLanguage": lang,
    "dateModified": lastModified, // Add freshness information
    "isPartOf": {
      "@type": "WebSite",
      "name": "YourCarpet.com",
      "url": baseUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl,
      "dateModified": lastModified // Add freshness information here too
    },
    "about": {
      "@type": "Product",
      "name": pageTitle || "Rugs and Carpets",
      "category": "Home Goods > Flooring > Carpets and Rugs"
    }
  };

  // Add subtitle as alternateName if available
  if (pageSubtitle) {
    jsonLD.alternateName = pageSubtitle;
  }

  // Add product list if we have products
  if (itemListElement.length > 0) {
    jsonLD.mainEntity = {
      "@type": "ItemList",
      "itemListElement": itemListElement,
      "numberOfItems": itemListElement.length
    };

    // Add aggregate offer information based on product data
    jsonLD.offers = {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "offerCount": itemListElement.length
    };
  }

  return JSON.stringify(jsonLD);
}
